import React, { useState, useEffect, useRef } from 'react';
import { useTranslations } from '../utils/translations';
// import { storage } from '../utils/storage'; // No longer needed - using API data only
import './AudioBar.css';

const AudioBar = ({
  audioSrc,
  onAudioComplete,
  isAudioMuted,
  onNext,
  onPrevious,
  onExit,
  canGoNext,
  canGoPrevious,
  currentSlide,
  currentLanguage,
  onStopAudio,
  isQuizAudioDisabled,
  onVisibilityChange,
  isSlideCompleted = false // NEW: Indicates if slide is already completed (before startSlide)
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [canSeek, setCanSeek] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
  const [maxPlayedTime, setMaxPlayedTime] = useState(0);
  // Listening time tracking (for OS-level seek detection)
  // IMPORTANT: OS-level media controls (notification bar, lock screen) cannot be disabled.
  // Users can seek forward using these controls, bypassing our UI-level restrictions.
  // This listening-time tracker ensures users must actually listen to the audio,
  // matching industry-standard LMS behavior (Coursera, Udemy, corporate LMS).
  const [listenedSeconds, setListenedSeconds] = useState(0);
  // Quiz audio states
  const [isQuizAudio, setIsQuizAudio] = useState(false);
  const [quizAudioQueue, setQuizAudioQueue] = useState([]);
  const [currentQuizAudioIndex, setCurrentQuizAudioIndex] = useState(0);
  // Auto-hide functionality
  const [isVisible, setIsVisible] = useState(true);
  const audioRef = useRef(null);
  const currentQuizHandlers = useRef({ ended: null, error: null });
  const hideTimeoutRef = useRef(null);
  // Refs for listening time tracking
  const lastValidTimeRef = useRef(0); // Last valid time before a seek
  const isSeekingRef = useRef(false); // Whether we're currently in a seeking state
  const lastTimeUpdateRef = useRef(Date.now()); // Timestamp of last time update
  const lastCurrentTimeRef = useRef(0); // Last currentTime value for delta calculation
  const listeningTimeIntervalRef = useRef(null); // Interval for tracking listening time
  const listenedSecondsRef = useRef(0); // Current listened seconds (for synchronous access)

  const { t } = useTranslations(currentLanguage);

  // Notify parent when visibility changes
  useEffect(() => {
    if (onVisibilityChange) {
      onVisibilityChange(isVisible);
    }
  }, [isVisible, onVisibilityChange]);

  // Auto-hide functionality
  useEffect(() => {
    const handleInteraction = () => {
      setIsVisible(true);
      
      // Clear existing timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      
      // Set new timeout to hide after 3 seconds of inactivity
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 3000);
    };

    // Add event listeners for user interactions
    const events = ['click', 'touchstart', 'mousemove', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, handleInteraction);
    });

    // Initial timeout
    handleInteraction();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    
    console.log('AudioBar useEffect triggered - audioSrc:', audioSrc, 'language:', currentLanguage);
    
    // Always reset ALL states when audio source changes or language changes
    setIsLoaded(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setHasCompleted(false);
    setCanSeek(false);
    setHasStartedPlaying(false);
    setMaxPlayedTime(0);
    // Reset listening time tracking
    setListenedSeconds(0);
    listenedSecondsRef.current = 0;
    lastValidTimeRef.current = 0;
    isSeekingRef.current = false;
    lastTimeUpdateRef.current = Date.now();
    lastCurrentTimeRef.current = 0;
    if (listeningTimeIntervalRef.current) {
      clearInterval(listeningTimeIntervalRef.current);
      listeningTimeIntervalRef.current = null;
    }
    
    // Always reset quiz audio states first
    setIsQuizAudio(false);
    setQuizAudioQueue([]);
    setCurrentQuizAudioIndex(0);
    
    if (!audio) return;
    
    // If language is empty (during revisit reload), don't process audio yet
    if (!currentLanguage) {
      console.log('Language is empty, skipping audio setup');
      return;
    }
    
    // Check if audioSrc is an array (quiz audio) or single file
    const isQuizAudioSource = Array.isArray(audioSrc);
    
    if (isQuizAudioSource && audioSrc.length > 0) {
      // Handle quiz audio queue
      setIsQuizAudio(true);
      setQuizAudioQueue(audioSrc);
      setCurrentQuizAudioIndex(0);
      
      // Set up the first audio but don't play yet
      try {
        // Set muted state before loading
        audio.muted = isAudioMuted || false;
        audio.src = audioSrc[0];
        audio.load();
        console.log('Quiz audio source set successfully:', audioSrc[0], '(muted:', isAudioMuted, ')');
      } catch (error) {
        console.error('Failed to set quiz audio source:', error);
        setIsLoaded(false);
      }
      
      // Enable audio controls for quiz
      setIsLoaded(true);
      setCanSeek(true); // Enable seeking for quiz audio (same as regular audio)
      
      // Add basic event listeners for quiz audio to enable seeking and controls
      audio.addEventListener('loadedmetadata', handleQuizLoadedMetadata);
      audio.addEventListener('timeupdate', handleQuizTimeUpdate);
      audio.addEventListener('play', () => setIsPlaying(true));
      audio.addEventListener('pause', () => setIsPlaying(false));
      
      console.log('Quiz audio loaded:', audioSrc);
      
      // DISABLED AUTO-PLAY for quiz slides - user will manually start
      console.log('Quiz audio loaded - auto-play disabled, user must manually start');
      
      return;
    }
    
    // If no audio source, ensure audio is properly reset but keep controls visible
    if (!audioSrc) {
      audio.pause();
      audio.currentTime = 0;
      audio.src = '';
      setIsLoaded(false); // Audio controls will be disabled but visible
      setIsQuizAudio(false);
      setQuizAudioQueue([]);
      return;
    }

    // If we have a valid audio source, set isLoaded to true immediately
    // This ensures controls are enabled even if metadata loading fails
    console.log('Setting isLoaded to true for valid audio source:', audioSrc);
    setIsLoaded(true);

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoaded(true);
      setCurrentTime(0);
      setHasCompleted(false);
      
      // Check if this slide's audio has been completed before
      // Audio completion handled by parent component (no localStorage)
      setCanSeek(false);
      setHasCompleted(false);
      
      // AUTO-PLAY: Start audio automatically with 2-second delay
      // Audio should play even when muted (muted only affects volume, not playback)
      setTimeout(() => {
        if (audio) {
          // Set muted state before playing
          audio.muted = isAudioMuted || false;
          audio.play()
            .then(() => {
              console.log('Auto-playing audio after 2-second delay (muted:', isAudioMuted, ')');
              setIsPlaying(true);
            })
            .catch(error => {
              console.log('Auto-play prevented by browser:', error);
            });
        }
      }, 2000); // 2-second delay
    };

    const handleTimeUpdate = () => {
      const newTime = audio.currentTime;
      setCurrentTime(newTime);
      
      // Track the maximum time reached during first play
      if (!hasCompleted) {
        setMaxPlayedTime(prev => Math.max(prev, newTime));
      }
    };

    /**
     * LISTENING TIME TRACKING - Industry-Standard LMS Validation
     * 
     * PROBLEM: OS-level media controls (notification bar, lock screen) cannot be disabled.
     * Users can seek forward using these controls, bypassing our UI-level forward-seek restrictions.
     * When users seek forward via OS controls, the audio ends and onAudioComplete() fires,
     * incorrectly enabling the "Next" button and bypassing the learning requirement.
     * 
     * SOLUTION: Implement actual listening time validation (Coursera/Udemy-style).
     * This tracks the actual time the user has listened to the audio, independent of
     * playback position. Forward seek jumps are detected and the skipped time is not counted.
     * 
     * HOW IT WORKS:
     * 1. Tracks listenedSeconds counter that increments ONLY when:
     *    - Audio is actively playing (not paused)
     *    - Playback rate is 1x (not fast-forwarded)
     *    - Time progresses forward naturally (small deltas, not jumps)
     *    - Not in a seeking state
     * 2. Detects forward seek jumps via onseeking/onseeked events
     * 3. Ignores skipped time from forward seeks (OS-level or programmatic)
     * 4. Allows backward seeks without penalty
     * 5. Enables "Next" button ONLY when listenedSeconds >= duration - tolerance
     * 
     * This ensures users must actually listen to the audio, even if they use OS controls
     * to seek forward. The existing UI-level forward-seek restriction remains unchanged
     * and works as a first layer of protection.
     */
    const trackListeningTime = () => {
      const audio = audioRef.current;
      if (!audio || !audio.src || !audio.duration || isNaN(audio.duration) || audio.duration === 0) {
        return;
      }

      const now = Date.now();
      const currentTime = audio.currentTime;
      const audioDuration = audio.duration;
      const timeSinceLastUpdate = (now - lastTimeUpdateRef.current) / 1000; // Convert to seconds
      
      // Only track listening time when:
      // 1. Audio is actively playing
      // 2. Playback rate is exactly 1x (not fast-forwarded)
      // 3. Not in a seeking state
      // 4. Time is progressing forward naturally (small deltas only)
      const isPlaying = !audio.paused;
      const isNormalSpeed = Math.abs(audio.playbackRate - 1.0) < 0.01;
      const isNotSeeking = !isSeekingRef.current;
      
      // Calculate time delta - should be small and forward
      const timeDelta = currentTime - lastCurrentTimeRef.current;
      const isForwardProgress = timeDelta > 0 && timeDelta < 2.0; // Allow up to 2s jumps (buffering tolerance)
      const isReasonableDelta = Math.abs(timeDelta - timeSinceLastUpdate) < 1.0; // Within 1s of expected
      
      if (isPlaying && isNormalSpeed && isNotSeeking && isForwardProgress && isReasonableDelta) {
        // Increment listening time by the actual time delta (not timeSinceLastUpdate)
        // This ensures we only count time that was actually listened to
        setListenedSeconds(prev => {
          const newListened = prev + timeDelta;
          // Cap at duration to prevent overflow
          const capped = Math.min(newListened, audioDuration);
          // Update ref for synchronous access
          listenedSecondsRef.current = capped;
          return capped;
        });
      }
      
      // Update refs for next iteration
      lastTimeUpdateRef.current = now;
      lastCurrentTimeRef.current = currentTime;
    };

    /**
     * Handle seeking events to detect forward jumps (from OS controls or programmatic seeks)
     * 
     * When a seek occurs (via OS notification controls, lock screen, or programmatic),
     * we mark the seeking state and store the last valid time. This allows us to detect
     * forward jumps in handleSeeked() and prevent skipped time from being counted.
     */
    const handleSeeking = () => {
      const audio = audioRef.current;
      if (!audio) return;
      
      // Mark that we're in a seeking state
      isSeekingRef.current = true;
      
      // Store the last valid time before the seek
      lastValidTimeRef.current = lastCurrentTimeRef.current;
    };

    /**
     * Handle seek completion - detect and handle forward/backward seek jumps
     * 
     * After a seek completes, we check if it was a forward jump (more than 2 seconds).
     * Forward jumps indicate the user skipped content (via OS controls), so that time
     * is NOT counted toward listening time. Backward seeks are allowed without penalty.
     * 
     * Note: We don't restore playback position for forward seeks because:
     * 1. OS-level seeks cannot be prevented
     * 2. Restoring would create bad UX (audio jumping back)
     * 3. Instead, we simply don't count the skipped time, requiring users to listen
     *    to the full duration naturally
     */
    const handleSeeked = () => {
      const audio = audioRef.current;
      if (!audio) return;
      
      const newTime = audio.currentTime;
      const lastTime = lastValidTimeRef.current;
      const jumpSize = newTime - lastTime;
      
      // Detect forward seek jumps (OS-level or programmatic)
      // If user seeks forward more than 2 seconds, that time was NOT listened to
      if (jumpSize > 2.0) {
        // Large forward jump detected - this time was skipped, don't count it
        console.log(`[ListeningTime] Forward seek detected: jumped from ${lastTime.toFixed(2)}s to ${newTime.toFixed(2)}s (skipped ${jumpSize.toFixed(2)}s)`);
        // We don't restore playback position - allow the seek but don't count the time
        // This way users can seek via OS controls, but must still listen to full duration
      } else if (jumpSize < -0.5) {
        // Backward seek - allowed, no penalty, just update tracking
        console.log(`[ListeningTime] Backward seek detected: jumped from ${lastTime.toFixed(2)}s to ${newTime.toFixed(2)}s`);
      }
      
      // Update current time reference
      lastCurrentTimeRef.current = newTime;
      lastTimeUpdateRef.current = Date.now();
      
      // Clear seeking flag
      isSeekingRef.current = false;
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(audio.duration); // Use audio.duration instead of state duration
      setDuration(audio.duration); // Update duration state to ensure consistency
      
      // IMPORTANT: Don't automatically mark as completed when audio ends
      // The isAudioCompleted() function will check listening time and mark as completed
      // only when the user has actually listened to enough of the audio.
      // This prevents users from seeking to the end via OS controls and bypassing the requirement.
      
      // Check if listening time requirement is met
      // Use ref for synchronous access (closure might have stale value)
      const LISTENING_TIME_TOLERANCE = 1.0;
      const currentListenedSeconds = listenedSecondsRef.current;
      const hasListenedEnough = currentListenedSeconds >= (audio.duration - LISTENING_TIME_TOLERANCE);
      
      if (hasListenedEnough) {
        setHasCompleted(true);
        setCanSeek(true); // Enable seeking after completion
        onAudioComplete();
      } else {
        // Audio ended but listening time not met - don't mark as completed
        // This can happen if user seeks to end via OS controls
        console.log(`[ListeningTime] Audio ended but listening time not met: ${currentListenedSeconds.toFixed(2)}s / ${audio.duration.toFixed(2)}s`);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setHasStartedPlaying(true);
      
      // Initialize listening time tracking refs when playback starts
      if (audio) {
        lastCurrentTimeRef.current = audio.currentTime;
        lastTimeUpdateRef.current = Date.now();
        isSeekingRef.current = false;
      }
    };
    const handlePause = () => setIsPlaying(false);
    const handleError = (e) => {
      setIsLoaded(false);
    };

    // Set up event listeners only for regular audio (not quiz audio)
    if (!isQuizAudio) {
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('error', handleError);
      // Add seeking/seeked listeners for OS-level seek detection
      audio.addEventListener('seeking', handleSeeking);
      audio.addEventListener('seeked', handleSeeked);
      
      // Set up interval for listening time tracking (runs every 100ms for accuracy)
      // This is more reliable than relying solely on timeupdate events
      listeningTimeIntervalRef.current = setInterval(trackListeningTime, 100);
      
      // Explicitly load the audio
      console.log('Loading regular audio:', audioSrc);
      try {
        // Set muted state before loading
        audio.muted = isAudioMuted || false;
        audio.src = audioSrc;
        audio.load();
        console.log('Audio source set successfully:', audioSrc, '(muted:', isAudioMuted, ')');
      } catch (error) {
        console.error('Failed to set audio source:', error);
        setIsLoaded(false);
      }
      
      // Add a fallback in case loadedmetadata doesn't fire
      setTimeout(() => {
        console.log('Checking fallback for audio loading...');
        setIsLoaded(true); // Enable controls even if metadata doesn't load
        
        // AUTO-PLAY: Start regular audio automatically with 2-second delay
        // Audio should play even when muted (muted only affects volume, not playback)
        setTimeout(() => {
          if (audio && audio.src) {
            // Set muted state before playing
            audio.muted = isAudioMuted || false;
            audio.play()
              .then(() => {
                console.log('Auto-playing regular audio after 2-second delay (fallback, muted:', isAudioMuted, ')');
                setIsPlaying(true);
              })
              .catch(error => {
                console.log('Auto-play prevented by browser:', error);
              });
          }
        }, 2000); // 2-second delay for auto-play
      }, 1000);
    }

    return () => {
      if (audio) {
        // Remove all possible event listeners to prevent any issues
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('error', handleError);
        // Remove seeking/seeked listeners
        audio.removeEventListener('seeking', handleSeeking);
        audio.removeEventListener('seeked', handleSeeked);
        
        // For quiz audio, remove quiz-specific event listeners
        if (currentQuizHandlers.current.ended) {
          audio.removeEventListener('ended', currentQuizHandlers.current.ended);
        }
        if (currentQuizHandlers.current.error) {
          audio.removeEventListener('error', currentQuizHandlers.current.error);
        }
        audio.removeEventListener('timeupdate', handleQuizTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleQuizLoadedMetadata);
        audio.removeEventListener('play', () => setIsPlaying(true));
        audio.removeEventListener('pause', () => setIsPlaying(false));
        
        // Clear quiz handlers reference
        currentQuizHandlers.current = { ended: null, error: null };
        
        // Clear listening time tracking interval
        if (listeningTimeIntervalRef.current) {
          clearInterval(listeningTimeIntervalRef.current);
          listeningTimeIntervalRef.current = null;
        }
        
        // Ensure audio is stopped when component unmounts or audio changes
        audio.pause();
        audio.currentTime = 0;
        audio.src = '';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioSrc, onAudioComplete, currentLanguage, currentSlide?.SlideNumber]);

  useEffect(() => {
    // Only handle muting if we have a valid audio element with a source
    if (audioRef.current && audioRef.current.src) {
      console.log('Setting audio muted state to:', isAudioMuted);
      
      // Simply set the muted property - this should not affect playback position or state
      // Muted state only affects volume, not whether audio is playing
      audioRef.current.muted = isAudioMuted || false;
      
      console.log('Audio mute state changed successfully. Current time:', audioRef.current.currentTime, 'Paused:', audioRef.current.paused);
    }
  }, [isAudioMuted]);

  // Reset completion state when slide changes
  useEffect(() => {
    setHasCompleted(false);
    setCurrentTime(0);
    setIsPlaying(false);
    setCanSeek(false);
    
    // Reset listening time tracking when slide changes
    setListenedSeconds(0);
    listenedSecondsRef.current = 0;
    lastValidTimeRef.current = 0;
    isSeekingRef.current = false;
    lastTimeUpdateRef.current = Date.now();
    lastCurrentTimeRef.current = 0;
    
    // Check if this slide's audio has been previously completed
    // Audio completion handled by parent component (no localStorage)
  }, [currentSlide?.SlideNumber]);

  // Handle visibility changes (app background/foreground) for mobile devices
  // When app goes to background, browser may pause audio, but we want to ensure
  // tracking doesn't continue incorrectly. The trackListeningTime function already
  // checks audio.paused, but this provides additional safety.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // App went to background - audio is likely paused by browser
        // The trackListeningTime function will handle this, but we log for debugging
        console.log('[ListeningTime] App went to background');
      } else {
        // App came to foreground - resume tracking if audio is playing
        console.log('[ListeningTime] App came to foreground');
        // Update refs to current state to avoid false jump detection
        if (audioRef.current) {
          lastCurrentTimeRef.current = audioRef.current.currentTime;
          lastTimeUpdateRef.current = Date.now();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Function to stop audio (called from parent components)
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
      
      // Reset listening time tracking
      setListenedSeconds(0);
      listenedSecondsRef.current = 0;
      lastValidTimeRef.current = 0;
      isSeekingRef.current = false;
      lastTimeUpdateRef.current = Date.now();
      lastCurrentTimeRef.current = 0;
      
      // Reset quiz audio states completely
      setCurrentQuizAudioIndex(0);
      setIsQuizAudio(false);
      setQuizAudioQueue([]);
      
      // Clear any existing event handlers
      if (currentQuizHandlers.current.ended) {
        audioRef.current.removeEventListener('ended', currentQuizHandlers.current.ended);
      }
      if (currentQuizHandlers.current.error) {
        audioRef.current.removeEventListener('error', currentQuizHandlers.current.error);
      }
      currentQuizHandlers.current = { ended: null, error: null };
    }
  };

  // Expose stop function to parent via prop
  useEffect(() => {
    if (onStopAudio) {
      onStopAudio(stopAudio);
    }
  }, [onStopAudio]);

  const togglePlayPause = () => {
    if (!audioRef.current) {
      console.error('Audio ref is null');
      return;
    }

    // Check if we have a valid audio source
    if (!audioRef.current.src) {
      console.error('No audio source loaded');
      return;
    }

    // Double-check if we're dealing with quiz audio based on current audioSrc
    const isCurrentlyQuizAudio = Array.isArray(audioSrc) && audioSrc.length > 0;
    
    if (isCurrentlyQuizAudio && isQuizAudio) {
      // Handle quiz audio sequence - allow pause/resume like regular audio
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        // If we're at the beginning or haven't started, start the sequence
        // Otherwise, just resume the current audio
        if (currentQuizAudioIndex === 0 && audioRef.current.currentTime === 0) {
          playQuizAudioSequence();
        } else {
          audioRef.current.play()
            .then(() => setIsPlaying(true))
            .catch(error => console.error('Quiz audio resume failed:', error));
        }
      }
    } else {
      // Handle regular audio
      if (!isLoaded) return;
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const playQuizAudioSequence = () => {
    if (quizAudioQueue.length === 0) return;
    
    console.log('Starting quiz audio sequence...');
    console.log('Quiz audio queue:', quizAudioQueue);
    playQuizAudioAtIndex(0);
  };

  const playQuizAudioAtIndex = (index) => {
    if (index >= quizAudioQueue.length || !audioRef.current) {
      console.log('Quiz audio sequence completed');
      setIsPlaying(false);
      setCurrentQuizAudioIndex(0);
      return;
    }

    const audioType = index === 0 ? 'Question' : `Option ${index}`;
    console.log(`Playing ${audioType} audio (${index + 1}/${quizAudioQueue.length}): ${quizAudioQueue[index]}`);

    setCurrentQuizAudioIndex(index);
    
    // Reset time and duration for new audio
    setCurrentTime(0);
    setDuration(0);
    
    // Remove previous quiz event handlers if they exist
    if (currentQuizHandlers.current.ended) {
      audioRef.current.removeEventListener('ended', currentQuizHandlers.current.ended);
    }
    if (currentQuizHandlers.current.error) {
      audioRef.current.removeEventListener('error', currentQuizHandlers.current.error);
    }
    
    // Set new audio source
    audioRef.current.src = quizAudioQueue[index];
    audioRef.current.load();
    
    // Create new event handlers for this specific audio index
    const endedHandler = () => handleQuizAudioEndedForIndex(index);
    const errorHandler = () => handleQuizAudioErrorForIndex(index);
    
    // Store references to the handlers so we can remove them later
    currentQuizHandlers.current.ended = endedHandler;
    currentQuizHandlers.current.error = errorHandler;
    
    // Add event listeners for this audio
    audioRef.current.addEventListener('ended', endedHandler);
    audioRef.current.addEventListener('error', errorHandler);
    audioRef.current.addEventListener('timeupdate', handleQuizTimeUpdate);
    audioRef.current.addEventListener('loadedmetadata', handleQuizLoadedMetadata);
    
    audioRef.current.play()
      .then(() => {
        console.log(`Successfully started playing quiz audio ${index + 1}`);
        setIsPlaying(true);
      })
      .catch(error => {
        console.error('Quiz audio play failed:', error);
        // Try next audio if this one fails
        setTimeout(() => playQuizAudioAtIndex(index + 1), 500);
      });
  };

  const handleQuizAudioEndedForIndex = (currentIndex) => {
    console.log(`handleQuizAudioEndedForIndex called for index: ${currentIndex}, Queue length: ${quizAudioQueue.length}`);
    const nextIndex = currentIndex + 1;
    console.log(`Quiz audio ${currentIndex + 1} ended. Playing next: ${nextIndex + 1}`);
    
    if (nextIndex < quizAudioQueue.length) {
      console.log(`Moving to next audio: ${quizAudioQueue[nextIndex]}`);
      // Small delay between audios
      setTimeout(() => playQuizAudioAtIndex(nextIndex), 500);
    } else {
      console.log('All quiz audios completed');
      setIsPlaying(false);
      setCurrentQuizAudioIndex(0);
      setHasCompleted(true); // Enable forward button after quiz completion
      setCanSeek(true); // Enable seeking after completion
      
      // Audio completion handled by parent component (no localStorage)
      
      onAudioComplete();
    }
  };

  const handleQuizAudioErrorForIndex = (currentIndex) => {
    console.error(`Quiz audio error for index ${currentIndex}`);
    // Try next audio
    const nextIndex = currentIndex + 1;
    if (nextIndex < quizAudioQueue.length) {
      setTimeout(() => playQuizAudioAtIndex(nextIndex), 500);
    } else {
      setIsPlaying(false);
      setCurrentQuizAudioIndex(0);
      setHasCompleted(true); // Enable forward button after quiz completion (even with errors)
      setCanSeek(true); // Enable seeking after completion
    }
  };



  const handleQuizTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleQuizLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current || !isLoaded) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const seekTime = (clickX / width) * duration;
    
    // Enhanced seeking restrictions for first-time play
    if (hasCompleted || isSlideCompleted) {
      // Audio completed OR slide already completed - allow seeking anywhere
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    } else if (hasStartedPlaying) {
      // First-time play in progress - only allow seeking backward or up to max played time
      const maxAllowedTime = Math.max(maxPlayedTime, currentTime);
      if (seekTime <= maxAllowedTime) {
        audioRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);
      }
    } else {
      // Audio not started yet - only allow seeking to beginning
      if (seekTime <= 0.1) { // Allow small tolerance for clicking at start
        audioRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);
      }
    }
  };

  const handleRewind = () => {
    if (!audioRef.current || !isLoaded) return;
    const newTime = Math.max(0, currentTime - 5);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleForward = () => {
    if (!audioRef.current || !isLoaded) return;
    
    // Allow forward for completed slides or if seeking is allowed
    if (!canSeek && !isSlideCompleted) return;
    
    if (isSlideCompleted) {
      console.log('AudioBar: Forward button enabled for completed slide');
    }
    
    const newTime = Math.min(duration, currentTime + 5);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Get content title from slide using translations
  const getContentTitle = () => {
    if (!currentSlide) return t('ui.slideTitles.default');
    return t(`ui.slideTitles.${currentSlide.SlideNumber}`) || t('ui.slideTitles.default');
  };

  // Check if audio is completed for this slide
  const isAudioCompleted = () => {
    // IMPORTANT: If slide is already completed (before startSlide), always return true
    if (isSlideCompleted) {
      console.log('AudioBar: Slide is already completed (before startSlide), allowing navigation');
      return true;
    }
    
    if (!audioSrc || (Array.isArray(audioSrc) && audioSrc.length === 0)) return true; // No audio means completed
    
    // Check if current slide is actually a quiz slide
    const isCurrentSlideQuiz = currentSlide?.IsQuiz === true;
    const isCurrentlyQuizAudio = Array.isArray(audioSrc) && audioSrc.length > 0;
    
    if (isCurrentSlideQuiz && isCurrentlyQuizAudio && isQuizAudio) {
      // For quiz audio, completion is when all audios in the queue have been played
      return currentQuizAudioIndex >= quizAudioQueue.length - 1 && !isPlaying;
    }
    
    // For regular slides: Use listening time validation (industry-standard LMS logic)
    // This prevents users from bypassing learning requirements via OS-level media controls
    // Tolerance: Allow 1 second margin for timing precision and edge cases
    const LISTENING_TIME_TOLERANCE = 1.0;
    
    if (duration > 0) {
      // Check if user has listened to enough of the audio
      // Even if audio.onended fired or currentTime === duration, we require actual listening time
      const hasListenedEnough = listenedSeconds >= (duration - LISTENING_TIME_TOLERANCE);
      
      if (hasListenedEnough) {
        // If listening requirement is met, mark as completed (if not already)
        if (!hasCompleted) {
          setHasCompleted(true);
          setCanSeek(true);
          onAudioComplete();
        }
        return true;
      }
      
      // Listening time not met - Next button remains disabled
      // This works even if user seeks to end via OS controls
      return false;
    }
    
    // Fallback: if duration not loaded yet, use old logic
    return hasCompleted || (duration > 0 && currentTime >= duration - 0.5);
  };

  // Check if seeking should be enabled
  const isSeekingEnabled = () => {
    // IMPORTANT: If slide is already completed (before startSlide), allow full seeking
    if (isSlideCompleted) {
      return true;
    }
    
    if (!audioSrc || (Array.isArray(audioSrc) && audioSrc.length === 0)) return true; // No audio means all controls enabled
    return canSeek || hasCompleted || hasStartedPlaying;
  };

  if (!audioSrc || (Array.isArray(audioSrc) && audioSrc.length === 0)) {
      return (
    <div className={`audio-bar ${!isVisible ? 'hidden' : ''}`}>
        <div className="audio-bar-content">
          <div className="content-info">
            <div className="content-title">
              {getContentTitle()}
            </div>
          </div>
          <div className="audio-controls">
            <button className="rewind-btn" disabled={true}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
              </svg>
            </button>
            <button className="play-btn" disabled>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </button>
            <button className="forward-btn" disabled>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1l5 5-5 5v-4c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8v-4z"/>
              </svg>
            </button>
            <div className="timeline">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: '0%' }}></div>
              </div>
              <span className="time-display">0:00 / 0:00</span>
            </div>
          </div>
          <div className="navigation-controls">
            <button 
              className="nav-btn" 
              onClick={onPrevious}
              disabled={!canGoPrevious}
            >
              {t('ui.navigation.back')}
            </button>
            <button 
              className="nav-btn" 
              onClick={onNext}
              disabled={!canGoNext}
            >
              {t('ui.navigation.next')}
            </button>
            <button className="nav-btn exit" onClick={onExit}>
              {t('ui.navigation.exit')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <audio ref={audioRef} preload="metadata" />
      <div className={`audio-bar ${!isVisible ? 'hidden' : ''}`}>
        <div className="audio-bar-content">
          <div className="audio-controls">
            <button 
              className="rewind-btn" 
              onClick={handleRewind}
              disabled={!isLoaded}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
              </svg>
            </button>
            <button 
              className={`play-btn ${isPlaying ? 'playing' : ''}`}
              onClick={togglePlayPause}
              disabled={!isLoaded || isQuizAudioDisabled}
            >
              {isPlaying ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>
            <button 
              className="forward-btn" 
              onClick={handleForward}
              disabled={!isLoaded || !isSeekingEnabled() || (!hasCompleted && !canSeek && !isSlideCompleted)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1l5 5-5 5v-4c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8v-4z"/>
              </svg>
            </button>
          </div>
          <div 
            className={`timeline ${isSeekingEnabled() ? 'seekable' : 'no-seek'}`}
          >
            <div className="progress-bar-container" onClick={handleSeek}>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progressPercentage}%` }}
                ></div>
                <div 
                  className="progress-handle" 
                  style={{ left: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
            <span className="time-display">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
          <div className="navigation-controls">
            <button 
              className="nav-btn" 
              onClick={onPrevious}
              disabled={!canGoPrevious}
            >
              {t('ui.navigation.back')}
            </button>
            <button 
              className="nav-btn" 
              onClick={onNext}
              disabled={!canGoNext || !isAudioCompleted()}
            >
              {t('ui.navigation.next')}
            </button>
            <button className="nav-btn exit" onClick={onExit}>
              {t('ui.navigation.exit')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AudioBar; 